// Playing a flow on a real phone, from inside the editor.
//
// Authoring a flow blind — write twenty steps, assign them to a team, watch the registry
// for a failure — is how a locator typo costs a live payout. This bar runs the flow you
// are looking at (unsaved edits included) on the device the inspector is pointed at, and
// paints each step as the engine reaches it, so the flow is proven before it is assigned.
//
// The bar also carries the cache reset (the same modal the device card offers): the state
// the clicker keeps between runs is what makes a second run of the same flow behave unlike
// the first, and this is where that is noticed.
//
// Two modes, one session: ▶ runs to the end, ⏭ stops before every node so you can watch
// the phone between steps. ⏸ moves a running flow into the second mode without losing its
// place — it parks at the next node and ▶ carries on from there — while ⏹ is the reset: the
// run is aborted and the next ▶ starts from the first step. The session (an Appium driver
// held open on the phone) survives a finished run, so a second ▶ costs nothing — closing
// it is a separate, explicit ⏏.
//
// **Money.** A run plays the flow WHOLE, commit included — the transfer really goes out.
// There is no "stop before the money" mode: a flow proven up to the tap that pays proves
// nothing about the tap that pays. Author against a test account and a small amount.
//
// **One session owns the phone.** While a session is open the inspector's screen snapshot
// (the Screen window, `openScreen`) is taken through this same Appium session — see
// common/flow_debug.py. The editor must never read the screen over adb while a session
// is open; the two uiautomators fight and the run is what loses. The snapshot is taken
// only when the author presses Refresh — never by the run itself.
import { esc } from '../ui/modal';
import { showClickerCacheModal } from '../clicker/clicker-cache-modal';
import { t } from '../ui/lang';
import { startFlowRun, flowRunCommand, pollFlowRun, stopFlowRun, stopFlowRunOnUnload, opLabel, } from './flows-shared';
/** A step's duration, short enough to sit in a chip. */
export function formatRunMs(ms) {
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
/** A whole run's duration, where minutes are likely — "2m 14s" reads faster than "134.2s". */
function formatTotal(ms) {
    if (ms < 60000)
        return formatRunMs(ms);
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
/** How much of a locator the log shows before it starts eating the line. */
const LOG_LOCATOR_MAX = 90;
/** The lane a node already decided on, to carry over into its verdict mark. */
function laneOf(marks, path) {
    const prev = marks.get(path);
    return prev
        ? {
            ...(prev.lane ? { lane: prev.lane } : {}), ...(prev.laneText ? { laneText: prev.laneText } : {}),
            ...(prev.lanes?.length ? { lanes: prev.lanes } : {}),
            ...(prev.resp ? { resp: prev.resp } : {}),
        }
        : {};
}
/**
 * The USSD dialogs a `ussd` / `ussd_pay` step saw, in order — the engine records them as the
 * step's `resp`, a JSON array of strings. Null for any other response (take_tasks' JSON).
 */
export function ussdReplies(resp) {
    if (!resp)
        return null;
    try {
        const v = JSON.parse(resp);
        return Array.isArray(v) && v.every((r) => typeof r === 'string') ? v : null;
    }
    catch {
        return null;
    }
}
/** A branch event as log lines: the lane sentence, or what the USSD network answered. */
export function branchLogLines(ev) {
    const text = ev.text || ev.lane || '';
    if (text)
        return [`  ↳ ${text}`];
    const replies = ussdReplies(ev.resp);
    if (!replies)
        return [];
    return replies.length
        ? replies.map((r) => `  ↳ USSD: ${r.replace(/\s+/g, ' ')}`)
        : [`  ↳ ${t('pf.ussd_no_dialog')}`];
}
/** How many lane sentences one step keeps — a `for_each` walking a long list would
 *  otherwise carry the whole page of rows. */
const LANES_KEPT = 20;
/**
 * Fold ONE engine event into the step marks — the only place a mark is decided.
 *
 * A run watched live and a run read back from the registry are the same events in the same
 * order (the clicker records what the debugger streams), so they must produce the same
 * picture; two readers would have drifted the first time a lane rule changed. The live
 * panel calls this per polled event, the stored view over the whole trace.
 */
export function applyRunMark(marks, ev) {
    if (ev.path === undefined)
        return;
    switch (ev.e) {
        case 'at':
            marks.set(ev.path, { mark: 'at' });
            break;
        case 'branch':
            // A conditional chose its lane (or a repeat ended). Kept on the node's own mark,
            // so a lane that is EMPTY — "skip the detour" — still shows which way the run
            // went; the verdict that follows must not wipe it.
            {
                const prev = marks.get(ev.path) ?? { mark: 'at' };
                const text = ev.text ?? ev.lane ?? '';
                const lanes = prev.lanes ?? [];
                marks.set(ev.path, {
                    ...prev,
                    ...(ev.lane ? { lane: ev.lane } : {}), ...(text ? { laneText: text } : {}),
                    ...(ev.resp ? { resp: ev.resp } : {}),
                    lanes: (text && lanes[lanes.length - 1] !== text
                        ? [...lanes, text] : lanes).slice(-LANES_KEPT),
                });
            }
            break;
        case 'ok':
            marks.set(ev.path, {
                ...laneOf(marks, ev.path), mark: 'ok',
                ...(typeof ev.ms === 'number' ? { ms: ev.ms } : {}),
                ...(ev.value ? { value: ev.value } : {}),
            });
            break;
        case 'err':
            marks.set(ev.path, { ...laneOf(marks, ev.path), mark: 'err', error: ev.error ?? '' });
            break;
        default:
            break;
    }
}
/**
 * The whole picture of a finished run: every step it marked, with the ones it was still
 * inside cut off (`cut`) the way a live run closes them when it ends.
 *
 * `failedAt` is the engine's own `#3/then/1 tap` for the step that ENDED the operation — a
 * step can fail without that (best_effort), and an `outcome` step can end it without ever
 * raising, so the trace alone does not always mark where the run came apart.
 */
export function marksFromEvents(events, failedAt = '') {
    const marks = new Map();
    // The placeholder table, folded as the run goes: each event carries only what changed,
    // so the state AT a step is every delta up to and including it. The snapshot is copied
    // onto the step's mark — that is what the Context tab reads.
    const tokens = {};
    for (const ev of events) {
        applyRunMark(marks, ev);
        for (const [k, v] of Object.entries(ev.vars ?? {})) {
            if (v)
                tokens[k] = v;
            else
                delete tokens[k];
        }
        const at = ev.path !== undefined ? marks.get(ev.path) : undefined;
        if (at)
            marks.set(ev.path, { ...at, vars: { ...tokens } });
    }
    for (const [path, run] of marks) {
        // Keep the step's own placeholder snapshot: it was cut off, not un-run, and what the
        // tokens stood for when the run stopped inside it is the whole point of looking.
        if (run.mark === 'at')
            marks.set(path, { ...run, ...laneOf(marks, path), mark: 'cut' });
    }
    const path = failedAt.replace(/^#/, '').split(' ')[0] ?? '';
    const at = path ? marks.get(path) : undefined;
    if (at && at.mark !== 'err')
        marks.set(path, { ...at, mark: 'err', error: at.error ?? '' });
    return marks;
}
/**
 * The placeholder table as the run left it — what a step it never reached would have seen.
 *
 * Events carry only what changed, so the end state is every delta folded in order. Shown on
 * steps with no snapshot of their own, which is every step below where the run stopped.
 */
export function varsAtEnd(events) {
    const tokens = {};
    for (const ev of events) {
        for (const [k, v] of Object.entries(ev.vars ?? {})) {
            if (v)
                tokens[k] = v;
            else
                delete tokens[k];
        }
    }
    return tokens;
}
/** The node at `3/then/1`, or null when the document has changed under the run. */
export function nodeAtPath(steps, path) {
    const parts = path.split('/');
    let list = steps;
    let node = null;
    for (let i = 0; i < parts.length; i += 2) {
        node = list[Number(parts[i])] ?? null;
        if (!node)
            return null;
        const lane = parts[i + 1];
        if (lane)
            list = node[lane] ?? [];
    }
    return node;
}
/** What a step ACTS ON, for the log: its locator, and the value it types. */
function stepDetail(node) {
    if (!node)
        return '';
    const cut = (v) => (v.length > LOG_LOCATOR_MAX ? `${v.slice(0, LOG_LOCATOR_MAX)}…` : v);
    const locator = node.xpath ?? node.until ?? node.key_template ?? '';
    const parts = [
        locator ? cut(locator) : '',
        // A fill/keypad's value is the point of the step; a tap has none.
        node.value ? `= ${cut(node.value)}` : '',
        node.op === 'goto' && node.target ? `→ ${node.target}` : '',
        node.op === 'outcome' && node.outcome ? node.outcome : '',
        node.op === 'if_app_running' && node.package ? node.package : '',
    ].filter(Boolean);
    return parts.length ? ` · ${parts.join(' ')}` : '';
}
/**
 * A step's place in the flow, for the log: `3/then/1` reads as "4 > then > 2 - Tap".
 *
 * The step list shows a run as chips on the rows; the log has to say the same thing in
 * words, or a run reads as three lines (started, ready, finished) with the whole flow
 * missing in between. The locator comes with it — twelve "Tap" lines in a row say nothing
 * about WHICH element each one hit.
 */
export function stepLabel(path, op, node) {
    const parts = path.split('/');
    const out = [];
    for (let i = 0; i < parts.length; i += 2) {
        out.push(String(Number(parts[i]) + 1));
        const lane = parts[i + 1];
        if (lane)
            out.push(t(`pf.lane_${lane}`));
    }
    return `${out.join(' › ')}${op ? ` · ${opLabel(op)}` : ''}${stepDetail(node)}`;
}
const POLL_MS = 800;
/* The log is a whole page tab now, not a 130px strip — it can hold a long run's worth
   of lines, and a run that failed at step 30 is exactly when the early ones matter. */
const MAX_LOG = 300;
const PLAY_SVG = '<svg viewBox="0 0 24 24"><path d="M7 5v14l11-7z"/></svg>';
const STEP_SVG = '<svg viewBox="0 0 24 24"><path d="M6 5v14l9-7z"/><rect x="16" y="5" width="2.6" height="14" rx="1"/></svg>';
const PAUSE_SVG = '<svg viewBox="0 0 24 24"><rect x="7" y="5" width="3.4" height="14" rx="1.2"/><rect x="13.6" y="5" width="3.4" height="14" rx="1.2"/></svg>';
const STOP_SVG = '<svg viewBox="0 0 24 24"><rect x="7" y="7" width="10" height="10" rx="1.5"/></svg>';
const CACHES_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"/><path d="M10 11v6M14 11v6"/></svg>';
const EJECT_SVG = '<svg viewBox="0 0 24 24"><path d="M12 5 5 14h14z"/><rect x="5" y="17" width="14" height="2.4" rx="1"/></svg>';
// A phone, in the bar's filled style (the stream controls' stroked camera would sit oddly
// next to the filled play/stop glyphs here).
const SCREEN_SVG = '<svg viewBox="0 0 24 24"><path d="M8 2.5h8A2.5 2.5 0 0 1 18.5 5v14A2.5 2.5 0 0 1 16 21.5H8A2.5 2.5 0 0 1 5.5 19V5A2.5 2.5 0 0 1 8 2.5zm-.5 3.5v11h9V6z"/></svg>';
export function mountFlowRunPanel(host, opts) {
    const marks = new Map();
    const log = [];
    /** Whether the log view is scrolled to the newest line — see say(). */
    let atBottom = true;
    /** How many lines the cap has dropped off the front, so a remembered index still points
     *  at the same line (see settleStepLine). */
    let dropped = 0;
    /** The step line waiting for its verdict: where it was written, and what it says. */
    let pendingStep = null;
    let phase = 'off';
    let stepping = false;
    /** The run to send once the session reports it is ready (the driver takes ~30s). */
    let pending = null;
    let lastEvent = -1;
    let poller = null;
    let status = '';
    let error = '';
    /** When the current run was ordered — the log reports what the whole flow took.
     *  Wall clock, so a stepped run counts the time the author spent looking at the phone;
     *  that is what "how long did it take" means when you are the one stepping it. */
    let runStartedAt = 0;
    let paramsOpen = false;
    // Test values stand in for what a real task carries. Remembered per flow, in this
    // browser only: authoring is try-fix-try, and retyping the test account and the PIN on
    // every reload is the part of that loop nobody should be doing. They are a TEST
    // account's — never a real operator's — which is what makes storing them fine.
    const VALUES_KEY = opts.flowId ? `flow-run-values:${opts.flowId}` : '';
    const values = { account: '', amount: '', pin: '', password: '', bandMin: '', bandMax: '', shiftFrom: '', shiftTo: '' };
    if (VALUES_KEY) {
        try {
            const saved = JSON.parse(localStorage.getItem(VALUES_KEY) ?? '{}');
            for (const k of Object.keys(values)) {
                if (typeof saved[k] === 'string')
                    values[k] = saved[k];
            }
        }
        catch { /* nothing remembered, or storage is off — the fields just start empty */ }
    }
    const rememberValues = () => {
        if (!VALUES_KEY)
            return;
        try {
            localStorage.setItem(VALUES_KEY, JSON.stringify(values));
        }
        catch { /* storage is off */ }
    };
    // Open the panel when there is something remembered in it: those values are what the
    // next run will send, and a run that pays must never do it from fields nobody saw.
    paramsOpen = Object.values(values).some(Boolean);
    host.innerHTML = `
        <div class="fr">
            <div class="fr__bar">
                <button type="button" class="fr-btn fr-dev"></button>
                <button type="button" class="fr-btn fr-screen" title="${esc(t('pf.screen_hint'))}">${SCREEN_SVG}<span>${esc(t('pf.screen'))}</span></button>
                <button type="button" class="fr-btn fr-btn--go fr-run">${PLAY_SVG}<span>${esc(t('pf.run'))}</span></button>
                <button type="button" class="fr-btn fr-step">${STEP_SVG}<span>${esc(t('pf.run_step'))}</span></button>
                <button type="button" class="fr-btn fr-pause" title="${esc(t('pf.run_pause_hint'))}">${PAUSE_SVG}<span>${esc(t('pf.run_pause'))}</span></button>
                <button type="button" class="fr-btn fr-stop" title="${esc(t('pf.run_stop_hint'))}">${STOP_SVG}<span>${esc(t('pf.run_stop'))}</span></button>
                <button type="button" class="fr-btn fr-close" title="${esc(t('pf.run_close_hint'))}">${EJECT_SVG}</button>
                <button type="button" class="fr-btn fr-caches" title="${esc(t('clicker.caches_clear'))}">${CACHES_SVG}</button>
                <span class="fr__status"></span>
                <button type="button" class="fr-btn fr-values">${esc(t('pf.run_values'))} ▾</button>
            </div>
            <div class="fr__params" hidden></div>
            <div class="fr__log" hidden></div>
        </div>
    `;
    const devBtn = host.querySelector('.fr-dev');
    const screenBtn = host.querySelector('.fr-screen');
    const runBtn = host.querySelector('.fr-run');
    const stepBtn = host.querySelector('.fr-step');
    const pauseBtn = host.querySelector('.fr-pause');
    const stopBtn = host.querySelector('.fr-stop');
    const closeBtn = host.querySelector('.fr-close');
    const cachesBtn = host.querySelector('.fr-caches');
    const statusEl = host.querySelector('.fr__status');
    const valuesBtn = host.querySelector('.fr-values');
    const paramsEl = host.querySelector('.fr__params');
    const logEl = host.querySelector('.fr__log');
    // The log is rendered by this module but lives wherever the page wants it — under the
    // step list, so the bar stays one line and the canvas keeps the height.
    if (opts.logHost)
        opts.logHost.appendChild(logEl);
    logEl.addEventListener('scroll', () => {
        atBottom = logEl.scrollHeight - logEl.scrollTop - logEl.clientHeight < 24;
    });
    /**
     * Compact one line of the clicker's own logging.
     *
     * `common/logger.py` prints `2026-09-02 12:00:00 INFO     pkg.mod:file.py:120 text`, and
     * the date, the module and the line number push the actual message off the right edge.
     * Keep the clock and the level — everything else is noise while watching a run.
     */
    function compactClickerLine(line) {
        const m = /^\d{4}-\d\d-\d\d (\d\d:\d\d:\d\d)\s+(\w+)\s+\S+:\S+:\d+\s+([\s\S]*)$/.exec(line);
        return m ? `${m[1]} ${m[2].toLowerCase()} ${m[3]}` : line;
    }
    /** Redraw the log. */
    function paintLog() {
        logEl.hidden = false;
        logEl.innerHTML = log
            .map((l) => `<div${l.from === 'clicker' ? ' class="fr-log--clicker"' : ''}>${esc(l.line)}</div>`)
            .join('');
        if (atBottom)
            logEl.scrollTop = logEl.scrollHeight;
    }
    /** Append a line. `clicker` lines came from the process itself and render dimmer. */
    function say(line, from = 'run') {
        log.push({ line, from });
        if (log.length > MAX_LOG) {
            log.shift();
            dropped += 1;
        }
        // Stay pinned to the newest line only while the reader is already at the bottom —
        // scrolling up to read an early failure must not be yanked back by the next line.
        paintLog();
    }
    /**
     * Turn the "started this step" line into its verdict, in place.
     *
     * A step that is running and a step that took 1.9s are the same line of the story, so
     * the log keeps ONE line per step: `▸ 4 · Tap` while it runs, `✓ 4 · Tap — 1.9s` after.
     * `at` remembers where it wrote; a line already scrolled out of the cap is just appended
     * anew rather than being hunted for.
     */
    function settleStepLine(path, line) {
        const at = pendingStep && pendingStep.path === path ? pendingStep.index - dropped : -1;
        if (pendingStep && pendingStep.path === path)
            pendingStep = null;
        if (at >= 0 && at < log.length) {
            log[at] = { line, from: 'run' };
            paintLog();
        }
        else
            say(line);
    }
    /**
     * Close off whatever was still running when the run ended.
     *
     * A stop lands between two nodes at best, and inside a 10s locator wait at worst — the
     * step that was in flight gets no `ok` and no `err`, so its "running" chip and its `▸`
     * log line would stay on screen as if the flow were still going. Both become "did not
     * finish" the moment the run does.
     */
    const lanePart = (path) => laneOf(marks, path);
    function cutOpenSteps(reason) {
        let changed = false;
        for (const [path, run] of marks) {
            if (run.mark !== 'at')
                continue;
            marks.set(path, { ...lanePart(path), mark: 'cut' });
            changed = true;
        }
        if (pendingStep)
            settleStepLine(pendingStep.path, `⏹ ${pendingStep.label} — ${reason}`);
        if (changed)
            opts.onMarks();
    }
    function renderBar() {
        const dev = opts.device();
        devBtn.textContent = dev ? dev.label : t('pf.pick_device');
        devBtn.classList.toggle('fr-dev--set', !!dev);
        const busy = phase === 'starting' || phase === 'running';
        // Paused, ▶ is the resume — it says so, because "Run" on a run that is standing
        // still reads as "start it again from the top", which is the one thing it will not do.
        runBtn.disabled = !dev || busy;
        runBtn.innerHTML = `${PLAY_SVG}<span>${esc(t(phase === 'paused' ? 'pf.run_resume' : 'pf.run'))}</span>`;
        stepBtn.disabled = !dev || phase === 'starting' || phase === 'running';
        // Paused, the two buttons mean "one more node" and "let it finish" — so the step
        // button says Next and takes the accent, or a stepped run reads as a hang.
        stepBtn.innerHTML = `${STEP_SVG}<span>${esc(t(phase === 'paused' ? 'pf.run_next' : 'pf.run_step'))}</span>`;
        stepBtn.classList.toggle('fr-btn--go', phase === 'paused');
        runBtn.title = phase === 'paused' ? t('pf.run_resume_hint') : '';
        // Only a run that is actually walking can be parked: parked already, or not running
        // at all, and there is nothing to hold.
        pauseBtn.disabled = phase !== 'running';
        stopBtn.disabled = phase === 'off' || phase === 'ready';
        cachesBtn.disabled = !dev;
        closeBtn.hidden = phase === 'off';
        statusEl.className = `fr__status${error ? ' fr__status--err' : ''}`;
        statusEl.textContent = error || status;
    }
    function renderParams() {
        paramsEl.hidden = !paramsOpen;
        if (!paramsOpen)
            return;
        // Amount is typed in MAJOR units (what an operator reads off the task) and sent in
        // minor ones, which is the only unit the engine's amount tokens are built from.
        paramsEl.innerHTML = `
            <label class="fr-v fr-v--wide"><span>${esc(t('pf.run_account'))}</span>
                <input class="settings-input fr-i" data-v="account" value="${esc(values.account)}" /></label>
            <label class="fr-v"><span>${esc(t('pf.run_amount'))}</span>
                <input class="settings-input fr-i" data-v="amount" type="number" step="0.01" min="0" value="${esc(values.amount)}" /></label>
            <label class="fr-v"><span>${esc(t('pf.run_pin'))}</span>
                <input class="settings-input fr-i" data-v="pin" value="${esc(values.pin)}" /></label>
            <label class="fr-v"><span>${esc(t('pf.run_password'))}</span>
                <input class="settings-input fr-i" data-v="password" type="password" value="${esc(values.password)}" /></label>
            ${opts.taskType === 'auto' ? `
            <label class="fr-v"><span>${esc(t('pf.run_band_min'))}</span>
                <input class="settings-input fr-i" data-v="bandMin" type="number" step="0.01" min="0" value="${esc(values.bandMin)}" /></label>
            <label class="fr-v"><span>${esc(t('pf.run_band_max'))}</span>
                <input class="settings-input fr-i" data-v="bandMax" type="number" step="0.01" min="0" value="${esc(values.bandMax)}" /></label>
            <p class="settings-hint fr-note">${esc(t('pf.run_band_hint'))}</p>` : ''}
            ${opts.taskType === 'shift' ? `
            <label class="fr-v"><span>${esc(t('shifts.from'))}</span>
                <input class="settings-input fr-i" data-v="shiftFrom" type="date" value="${esc(values.shiftFrom)}" /></label>
            <label class="fr-v"><span>${esc(t('shifts.to'))}</span>
                <input class="settings-input fr-i" data-v="shiftTo" type="date" value="${esc(values.shiftTo)}" /></label>
            <p class="settings-hint fr-note">${esc(t('pf.run_shift_hint'))}</p>` : ''}
            <p class="settings-hint fr-note">${esc(t('pf.run_hint'))}</p>
        `;
        paramsEl.querySelectorAll('.fr-i').forEach((i) => {
            i.addEventListener('change', () => {
                values[i.dataset['v']] = i.value.trim();
                rememberValues();
            });
        });
    }
    function runParams() {
        const major = Number(values.amount);
        // Typed in MAJOR units, like the amount, and sent in minor ones — the only unit the
        // engine compares a balance in.
        const minors = (v) => {
            const n = Number(v);
            return v.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
        };
        const bandMin = minors(values.bandMin);
        const bandMax = minors(values.bandMax);
        return {
            ...(values.account ? { account: values.account } : {}),
            ...(Number.isFinite(major) && major > 0 ? { amount: Math.round(major * 100) } : {}),
            ...(values.pin ? { pin: values.pin } : {}),
            ...(values.password ? { password: values.password } : {}),
            ...(bandMin === undefined ? {} : { band_min: bandMin }),
            ...(bandMax === undefined ? {} : { band_max: bandMax }),
            ...(opts.taskType === 'shift' ? shiftPeriod() : {}),
        };
    }
    /** The test days: what was picked, else yesterday and today — a dry run films nothing. */
    function shiftPeriod() {
        const local = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        const now = new Date();
        return {
            shift_from: values.shiftFrom || local(new Date(now.getTime() - 24 * 3600000)),
            shift_to: values.shiftTo || local(now),
        };
    }
    // ── the session ────────────────────────────────────────────────────────────
    function apply(ev) {
        switch (ev.e) {
            case 'log':
                // The clicker's own output, forwarded line by line by the runner — what the
                // engine was actually doing between two step marks.
                if (ev.line)
                    say(compactClickerLine(ev.line), 'clicker');
                break;
            case 'ready':
                phase = 'ready';
                status = t('pf.run_ready');
                say(t('pf.run_ready'));
                if (pending) {
                    const p = pending;
                    pending = null;
                    void send({ c: 'run', step: p === 'step' });
                }
                break;
            case 'at':
                applyRunMark(marks, ev);
                if (ev.path !== undefined) {
                    const label = stepLabel(ev.path, ev.op ?? '', nodeAtPath(opts.doc().steps, ev.path));
                    say(`▸ ${label}`);
                    pendingStep = { path: ev.path, label, index: dropped + log.length - 1 };
                }
                phase = stepping ? 'paused' : 'running';
                status = t(stepping ? 'pf.run_paused_at' : 'pf.run_at', { op: ev.op ?? '' });
                opts.onMarks();
                break;
            case 'branch': {
                // A conditional chose its lane (or a repeat ended). Kept on the node's own
                // mark, so a lane that is EMPTY — "skip the detour" — still shows which way
                // the run went; the verdict that follows must not wipe it.
                applyRunMark(marks, ev);
                if (ev.path !== undefined)
                    branchLogLines(ev).forEach((line) => say(line));
                opts.onMarks();
                break;
            }
            case 'ok': {
                applyRunMark(marks, ev);
                const label = pendingStep && pendingStep.path === ev.path
                    ? pendingStep.label
                    : stepLabel(ev.path ?? '', ev.op ?? '', ev.path ? nodeAtPath(opts.doc().steps, ev.path) : null);
                settleStepLine(ev.path, `✓ ${label}${typeof ev.ms === 'number' ? ` — ${formatRunMs(ev.ms)}` : ''}`);
                if (phase === 'paused')
                    phase = 'running';
                opts.onMarks();
                break;
            }
            case 'err': {
                applyRunMark(marks, ev);
                const label = pendingStep && pendingStep.path === ev.path
                    ? pendingStep.label
                    : stepLabel(ev.path ?? '', ev.op ?? '', ev.path ? nodeAtPath(opts.doc().steps, ev.path) : null);
                settleStepLine(ev.path, `✕ ${label} — ${ev.error ?? ''}`);
                opts.onMarks();
                break;
            }
            case 'end': {
                phase = 'ready';
                stepping = false;
                cutOpenSteps(ev.status ?? '');
                const tail = [ev.comment, ev.balance ? `${t('pf.run_balance')}: ${ev.balance}` : '']
                    .filter(Boolean).join(' · ');
                status = t('pf.run_ended', { status: ev.status ?? '' });
                const total = runStartedAt ? ` · ${t('pf.run_total', { t: formatTotal(Date.now() - runStartedAt) })}` : '';
                runStartedAt = 0;
                say(`■ ${ev.status ?? ''}${tail ? ` — ${tail}` : ''}${total}`);
                break;
            }
            case 'fatal':
                phase = 'off';
                stepping = false;
                pending = null;
                error = ev.error ?? '';
                say(`✕ ${ev.error ?? ''}`);
                cutOpenSteps(ev.error ?? '');
                stopPolling();
                break;
            case 'closed':
                phase = 'off';
                stepping = false;
                pending = null;
                status = t('pf.run_closed');
                cutOpenSteps(t('pf.run_closed'));
                stopPolling();
                break;
        }
    }
    async function tick() {
        // The editor may have been navigated away from — nothing unmounts this bar, so the
        // poll loop watches for its own host going out of the document. A session left
        // behind still HOLDS the phone (Appium + UIA2, and no clicker may start on it), so
        // leaving the page closes it rather than waiting out the runner's idle sweep.
        if (!host.isConnected) {
            stopPolling();
            window.removeEventListener('pagehide', onPageHide);
            const left = opts.device();
            if (left && phase !== 'off')
                void stopFlowRun(left.udid);
            phase = 'off';
            return;
        }
        const dev = opts.device();
        if (!dev)
            return;
        try {
            const state = await pollFlowRun(dev.udid, lastEvent);
            if (state.state === 'none') {
                if (phase !== 'off') {
                    phase = 'off';
                    status = t('pf.run_closed');
                    stopPolling();
                }
            }
            else {
                for (const ev of state.events) {
                    lastEvent = Math.max(lastEvent, ev.n);
                    apply(ev);
                }
            }
        }
        catch (e) {
            error = e.message;
        }
        renderBar();
    }
    /**
     * The page itself is going — reload, tab close, a link out of the panel.
     *
     * Leaving the editor within the SPA is handled by the poll loop above, but a reload
     * takes this whole script down with it and nothing was left to notice: the run kept
     * walking the flow on the phone, commit and all, until the runner's idle sweep ended
     * it two minutes later. `pagehide` covers reload and close alike (`beforeunload` does
     * not fire reliably on mobile, and neither survives without keepalive).
     */
    const onPageHide = () => {
        const dev = opts.device();
        if (dev && phase !== 'off')
            stopFlowRunOnUnload(dev.udid);
    };
    window.addEventListener('pagehide', onPageHide);
    function startPolling() {
        if (poller !== null)
            return;
        poller = window.setInterval(() => { void tick(); }, POLL_MS);
    }
    function stopPolling() {
        if (poller === null)
            return;
        window.clearInterval(poller);
        poller = null;
    }
    async function send(cmd) {
        const dev = opts.device();
        if (!dev)
            return;
        try {
            await flowRunCommand(dev.udid, cmd);
        }
        catch (e) {
            error = e.message;
            renderBar();
        }
    }
    async function begin(step) {
        const dev = opts.device();
        if (!dev) {
            opts.pickDevice();
            return;
        }
        error = '';
        marks.clear();
        opts.onMarks();
        stepping = step;
        runStartedAt = Date.now();
        if (phase === 'ready') {
            // The session is already open — reuse the driver, but re-load the document
            // first: the whole point of a second run is that something was edited between
            // them, and the session is still holding the flow it was opened with.
            status = t('pf.run_go');
            phase = step ? 'paused' : 'running';
            renderBar();
            await send({ c: 'load', flow: opts.doc(), params: runParams() });
            await send({ c: 'run', step });
            return;
        }
        phase = 'starting';
        pending = step ? 'step' : 'run';
        lastEvent = -1;
        status = t('pf.run_starting');
        say(t('pf.run_starting'));
        renderBar();
        try {
            await startFlowRun(dev.udid, opts.doc(), runParams());
            startPolling();
        }
        catch (e) {
            phase = 'off';
            pending = null;
            error = e.message;
            renderBar();
        }
    }
    devBtn.addEventListener('click', () => opts.pickDevice());
    screenBtn.addEventListener('click', () => opts.openScreen());
    runBtn.addEventListener('click', () => {
        if (phase === 'paused') {
            // Resume: the engine is blocked inside the run, so this leaves step mode
            // rather than starting a second one.
            stepping = false;
            phase = 'running';
            renderBar();
            void send({ c: 'continue' });
            return;
        }
        void begin(false);
    });
    stepBtn.addEventListener('click', () => {
        // The same button starts a stepped run and releases the node it is paused on —
        // which is what "step" means in a debugger, and one button fewer on the bar.
        if (phase === 'paused') {
            phase = 'running';
            renderBar();
            void send({ c: 'next' });
            return;
        }
        void begin(true);
    });
    pauseBtn.addEventListener('click', () => {
        // Not a stop: the engine keeps the run, and simply holds it at the next node gate.
        // Which node that is depends on what it is doing right now — a locator wait or a
        // 5s pause has to finish first — so the bar says "pausing" until the `at` that
        // reports the park arrives and flips the phase.
        stepping = true;
        status = t('pf.run_pausing');
        renderBar();
        void send({ c: 'pause' });
    });
    stopBtn.addEventListener('click', () => {
        stepping = false;
        status = t('pf.run_stopping');
        renderBar();
        void send({ c: 'stop' });
    });
    closeBtn.addEventListener('click', () => {
        const dev = opts.device();
        phase = 'off';
        pending = null;
        stepping = false;
        status = t('pf.run_closed');
        stopPolling();
        renderBar();
        if (dev)
            void stopFlowRun(dev.udid);
    });
    // Right here rather than only on the device card: `if_account_cached` takes the cached
    // branch for good once a run has read the number, and the author who needs the other
    // branch back is the one looking at this bar.
    cachesBtn.addEventListener('click', () => {
        const dev = opts.device();
        if (dev)
            showClickerCacheModal(dev.udid, dev.label);
    });
    valuesBtn.addEventListener('click', () => {
        paramsOpen = !paramsOpen;
        valuesBtn.innerHTML = `${esc(t('pf.run_values'))} ${paramsOpen ? '▴' : '▾'}`;
        renderParams();
    });
    renderBar();
    valuesBtn.innerHTML = `${esc(t('pf.run_values'))} ${paramsOpen ? '▴' : '▾'}`;
    renderParams();
    return {
        step(path) { return marks.get(path) ?? null; },
        hasMarks() { return marks.size > 0; },
        refresh() { renderBar(); },
        say(line) { say(line); },
    };
}
